import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { fetchJSON } from "@/lib/api";
import { fetchEmployees } from "@/lib/employees";
import { PrintService } from "@/utils/PrintService";
import { useFuelRates } from "@/hooks/useFuelRates";

type FuelProduct = {
  id: string;
  product_name?: string;
  productName?: string;
  short_name?: string;
  shortName?: string;
  lfrn?: string;
  lfr?: string;
  is_active?: boolean;
  isActive?: boolean;
  isDeleted?: boolean;
};


type Employee = {
  id: string;
  full_name?: string;
  fullName?: string;
  employee_name?: string;
  name?: string;
};

type SaleEntry = {
  id: string;
  _id?: string;
  date: string;
  shift?: string;
  mobileNumber: string;
  name: string;
  billNo?: string;
  vehicleNo?: string;
  product?: string;
  price?: number;
  amount?: number;
  discount?: number;
  qty?: number;
  description?: string;
  collectionMode?: string;
  collectionType?: string;
  employee?: string;
  gstTin?: string;
  createdAt?: string;
};

const COLLECTION_MODES = ["Bank", "Cash", "Petro Card", "Swipe"];
const BANK_TYPES = ["BOB", "CANARA", "ICICI", "SBI", "HDFC", "AXIS"];
const PETRO_TYPES = ["CCMS", "Extra Reward"];

const initialForm = {
  date: new Date().toISOString().split("T")[0],
  shift: "S-1",
  mobileNumber: "",
  name: "",
  billNo: "",
  vehicleNo: "",
  productId: "",
  product: "",
  price: "",
  amount: "",
  discount: "",
  qty: "",
  description: "",
  collectionMode: "",
  collectionType: "",
  employee: "",
  gstTin: "",
};

/** Display label from master data only — no hardcoded MS/HSD/XP defaults */
function productShortName(p: FuelProduct): string {
  const short = (p.short_name || p.shortName || "").trim();
  if (short) return short.toUpperCase();
  return (p.product_name || p.productName || "—").trim();
}


function formatRowDate(value?: string): string {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd-MMM-yyyy");
  } catch {
    return "—";
  }
}

function employeeLabel(e: Employee): string {
  return e.full_name || e.fullName || e.employee_name || e.name || "—";
}

export default function GuestSale() {
  const [form, setForm] = useState(initialForm);
  const [entries, setEntries] = useState<SaleEntry[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState("All");
  const [activeTab, setActiveTab] = useState("S-1");
  const [gstPopup, setGstPopup] = useState(false);
  const [gstInput, setGstInput] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterMobile, setFilterMobile] = useState("");
  const [sendSms, setSendSms] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadEntries = async () => {
    try {
      const res = await fetchJSON("/api/regular-sales");
      const data = await res.json();
      if (data.ok) {
        setEntries(
          (data.rows || []).map((r: SaleEntry & { _id?: string }) => ({
            ...r,
            id: r._id || r.id,
            date: r.date ? String(r.date).slice(0, 10) : "",
          }))
        );
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadEntries();
    fetchEmployees().then((rows) => setEmployees(rows as Employee[]));
  }, []);

  const { rates: fuelRateBadges, getRateById, fuelProducts } = useFuelRates({ date: form.date });

  const hasAnyPrice = fuelRateBadges.some((b) => b.rate != null);

  const setField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const applyProduct = (productId: string) => {
    const fp = fuelProducts.find((p) => String(p.id) === String(productId));
    const label = fp ? productShortName(fp) : "";
    const price = getRateById(productId);
    setForm((prev) => {
      const next = {
        ...prev,
        productId,
        product: label,
        price: price != null ? String(price) : prev.price,
      };
      const qty = +next.qty || 0;
      const pr = +(price != null ? price : next.price) || 0;
      const disc = +next.discount || 0;
      next.amount = (qty * pr - disc).toFixed(3);
      return next;
    });
  };

  const recalcAmount = (next: typeof form) => {
    const qty = +next.qty || 0;
    const price = +next.price || 0;
    const discount = +next.discount || 0;
    return { ...next, amount: (qty * price - discount).toFixed(3) };
  };

  const handleCollectionMode = (val: string) => {
    setForm((prev) => ({ ...prev, collectionMode: val, collectionType: "" }));
  };

  const handleSubmit = async () => {
    if (!form.date || !form.mobileNumber || !form.name) {
      alert("Please fill Date, Mobile Number and Name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        shift: form.shift,
        mobileNumber: form.mobileNumber,
        name: form.name,
        billNo: form.billNo,
        vehicleNo: form.vehicleNo,
        product: form.product,
        price: Number(form.price) || 0,
        amount: Number(form.amount) || 0,
        discount: Number(form.discount) || 0,
        qty: Number(form.qty) || 0,
        description: form.description,
        collectionMode: form.collectionMode,
        collectionType: form.collectionType,
        employee: form.employee,
        gstTin: form.gstTin,
      };

      const url = editingId ? `/api/regular-sales/${editingId}` : "/api/regular-sales";
      const res = await fetchJSON(url, {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.ok) {
        loadEntries();
        setForm({ ...initialForm, date: form.date, shift: form.shift });
        setEditingId(null);
        setSendSms(false);
      } else {
        alert(data.error || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (entry: SaleEntry) => {
    const fp = fuelProducts.find((p) => {
      const entryProd = (entry.product || "").toUpperCase().trim();
      const short = (p.short_name || p.shortName || "").toUpperCase().trim();
      const full = (p.product_name || p.productName || "").toUpperCase().trim();
      return entryProd === short || entryProd === full || String(p.id) === String(entry.product);
    });
    setForm({
      date: entry.date?.slice(0, 10) || initialForm.date,
      shift: entry.shift || "S-1",
      mobileNumber: entry.mobileNumber || "",
      name: entry.name || "",
      billNo: entry.billNo || "",
      vehicleNo: entry.vehicleNo || "",
      productId: fp?.id || "",
      product: entry.product || "",
      price: String(entry.price ?? ""),
      amount: String(entry.amount ?? ""),
      discount: String(entry.discount ?? ""),
      qty: String(entry.qty ?? ""),
      description: entry.description || "",
      collectionMode: entry.collectionMode || "",
      collectionType: entry.collectionType || "",
      employee: entry.employee || "",
      gstTin: entry.gstTin || "",
    });
    setEditingId(entry.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this entry?")) return;
    const res = await fetchJSON(`/api/regular-sales/${id}`, {
      method: "DELETE",
    });
    const data = await res.json();
    if (data.ok) setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const filtered = entries.filter((e) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      e.name.toLowerCase().includes(q) ||
      e.mobileNumber.includes(q) ||
      (e.vehicleNo || "").toLowerCase().includes(q) ||
      (e.billNo || "").toLowerCase().includes(q);
    const matchesShift = e.shift === activeTab;
    const matchesMobile = !filterMobile || e.mobileNumber.includes(filterMobile);
    const matchesFrom = !fromDate || e.date >= fromDate;
    const matchesTo = !toDate || e.date <= toDate;
    return matchesSearch && matchesShift && matchesMobile && matchesFrom && matchesTo;
  });

  const visibleRows =
    itemsPerPage === "All" ? filtered : filtered.slice(0, Number(itemsPerPage) || filtered.length);

  const regularSalesTotal = filtered.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const showCollectionType =
    form.collectionMode === "Bank" ||
    form.collectionMode === "Petro Card" ||
    form.collectionMode === "Swipe";
  const collectionTypeOptions =
    form.collectionMode === "Bank" || form.collectionMode === "Swipe" ? BANK_TYPES : PETRO_TYPES;

  const knownMobiles = [...new Set(entries.map((e) => e.mobileNumber).filter(Boolean))];

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-6 py-4">
        <nav className="text-sm text-white" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2">
            <li><span className="text-blue-200">Dashboard</span></li>
            <li className="flex items-center">
              <span className="mx-2 text-blue-300">&gt;</span>
              <span className="text-white font-medium">Regular Customer Sales</span>
            </li>
          </ol>
        </nav>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {fuelRateBadges.length === 0 ? (
            <span className="text-blue-100 text-sm">
              No fuel products configured — add products in <strong>Fuel Products</strong>
            </span>
          ) : (
            fuelRateBadges.map((b) => (
              <span
                key={b.id}
                className="text-white text-sm"
                title={
                  b.rate == null
                    ? `Set LFR/KL in Fuel Products or Daily Sale Rate for ${form.date}`
                    : b.dailyRate != null
                      ? `Daily Sale Rate for ${form.date}${b.productName ? ` (${b.productName})` : ""}`
                      : b.lfrPrice != null
                        ? `LFR/KL from Fuel Products${b.productName ? ` (${b.productName})` : ""}`
                        : `Last used price from sales history${b.productName ? ` (${b.productName})` : ""}`
                }
              >
                <span className="font-medium">{b.code}</span>
                <span className="mx-1">:</span>
                <span
                  className={`font-bold px-2 py-0.5 rounded ${
                    b.rate != null ? "bg-cyan-500 text-white" : "bg-white/20 text-blue-100"
                  }`}
                >
                  {b.rate != null ? Number(b.rate).toFixed(3) : "—"}
                </span>
              </span>
            ))
          )}
        </div>
        {fuelRateBadges.length > 0 && !hasAnyPrice && (
          <p className="mt-2 text-blue-100 text-xs max-w-3xl">
            <strong>—</strong> means no price yet. Enter <strong>LFR/KL</strong> in Fuel Products or set
            open/close rates in <strong>Daily Sale Rate</strong> for {form.date}.
          </p>
        )}
      </div>

      <div className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <span className="text-gray-600 text-sm">Regular Sales</span>
            <p className="text-2xl font-bold text-blue-900">{regularSalesTotal.toFixed(2)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Choose Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setField("date", e.target.value)}
              className="border border-gray-300 rounded px-3 py-2"
            />
          </div>
        </div>

        <div className="bg-blue-900 p-6 rounded-lg mb-6">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setField("date", e.target.value)}
                className="w-full p-2 bg-blue-50 border border-blue-200 rounded"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Shift</label>
              <div className="flex gap-4 items-center h-10 text-white">
                {["S-1", "S-2"].map((s) => (
                  <label key={s} className="flex items-center gap-1 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="shift"
                      checked={form.shift === s}
                      onChange={() => setField("shift", s)}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Mobile Number</label>
              <input
                list="guest-mobile-list"
                value={form.mobileNumber}
                onChange={(e) => setField("mobileNumber", e.target.value)}
                className="w-full p-2 bg-blue-50 border border-blue-200 rounded"
              />
              <datalist id="guest-mobile-list">
                {knownMobiles.map((m) => (
                  <option key={m} value={m} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Name</label>
              <input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                className="w-full p-2 bg-blue-50 border border-blue-200 rounded"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Bill No</label>
              <input
                value={form.billNo}
                onChange={(e) => setField("billNo", e.target.value)}
                className="w-full p-2 bg-blue-50 border border-blue-200 rounded"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Vehicle No</label>
              <input
                value={form.vehicleNo}
                onChange={(e) => setField("vehicleNo", e.target.value)}
                className="w-full p-2 bg-blue-50 border border-blue-200 rounded"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Product</label>
              <select
                value={form.productId}
                onChange={(e) => applyProduct(e.target.value)}
                className="w-full p-2 bg-white border border-gray-300 rounded"
              >
                <option value="">— Select —</option>
                {fuelRateBadges.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code}
                    {b.productName ? ` — ${b.productName}` : ""}
                    {b.rate != null ? ` @ ${Number(b.rate).toFixed(3)}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Price</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => setForm((prev) => recalcAmount({ ...prev, price: e.target.value }))}
                className="w-full p-2 bg-white border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Amount ( ₹)</label>
              <input
                type="number"
                readOnly
                value={form.amount}
                className="w-full p-2 bg-gray-100 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Discount ( ₹)</label>
              <input
                type="number"
                value={form.discount}
                onChange={(e) =>
                  setForm((prev) => recalcAmount({ ...prev, discount: e.target.value }))
                }
                className="w-full p-2 bg-white border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Qty (Lts)</label>
              <input
                type="number"
                value={form.qty}
                onChange={(e) => setForm((prev) => recalcAmount({ ...prev, qty: e.target.value }))}
                className="w-full p-2 bg-white border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Description</label>
              <input
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                className="w-full p-2 bg-white border border-gray-300 rounded"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Collection Mode</label>
              <select
                value={form.collectionMode}
                onChange={(e) => handleCollectionMode(e.target.value)}
                className="w-full p-2 bg-white border border-gray-300 rounded"
              >
                <option value="">— Select —</option>
                {COLLECTION_MODES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            {showCollectionType && (
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Payment Type</label>
                <select
                  value={form.collectionType}
                  onChange={(e) => setField("collectionType", e.target.value)}
                  className="w-full p-2 bg-white border border-gray-300 rounded"
                >
                  <option value="">— Select —</option>
                  {collectionTypeOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-white text-sm font-medium mb-2 block">Employee</label>
              <select
                value={form.employee}
                onChange={(e) => setField("employee", e.target.value)}
                className="w-full p-2 bg-white border border-gray-300 rounded"
              >
                <option value="">— Select —</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={employeeLabel(emp)}>{employeeLabel(emp)}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3 pb-2">
              <label className="text-white text-sm flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
                Send SMS ?
              </label>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <button
                type="button"
                onClick={() => { setGstInput(form.gstTin); setGstPopup(true); }}
                className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded text-sm font-medium"
              >
                Fill GST
              </button>
              {form.gstTin && (
                <span className="text-blue-100 text-xs truncate max-w-[120px]" title={form.gstTin}>
                  {form.gstTin}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-center mt-6 gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-12 py-3 text-lg font-medium rounded-lg"
            >
              {saving ? "SAVING…" : editingId ? "UPDATE" : "SAVE"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => { setForm({ ...initialForm, date: form.date }); setEditingId(null); }}
                className="bg-gray-500 text-white px-8 py-3 rounded-lg"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="bg-gray-50 border rounded-lg p-4 mb-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="text-sm font-medium block mb-1">From Date</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border rounded px-3 py-2" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">To Date</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border rounded px-3 py-2" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Mobile No</label>
            <input
              list="filter-mobile-list"
              value={filterMobile}
              onChange={(e) => setFilterMobile(e.target.value)}
              placeholder="choose Mobile No"
              className="border rounded px-3 py-2 w-48"
            />
            <datalist id="filter-mobile-list">
              {knownMobiles.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </div>
        </div>

        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="flex border-b">
            {["S-1", "S-2"].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => { setActiveTab(tab); setField("shift", tab); }}
                className={`px-6 py-3 font-medium ${
                  activeTab === tab ? "bg-blue-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-4 border-b bg-gray-50 flex flex-wrap justify-between gap-3 items-center">
            <div className="flex items-center gap-2 text-sm">
              <span>Filter:</span>
              <input
                type="text"
                placeholder="Type to filter..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border rounded px-3 py-1 w-56"
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span>Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(e.target.value)}
                className="border rounded px-2 py-1"
              >
                <option value="All">All</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {[
                    "S.No", "Date", "Mobile No", "Name", "Bill Number", "Vehicle Number",
                    "Employee", "Product", "Payment Mode", "Payment Type", "Price", "Quantity",
                    "Cost ( ₹)", "Discount ( ₹)", "Action", "Shift", "Description", "User Log Details",
                  ].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={18} className="px-4 py-8 text-center text-gray-500">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((entry, i) => (
                    <tr key={entry.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">{i + 1}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatRowDate(entry.date)}</td>
                      <td className="px-3 py-2">{entry.mobileNumber}</td>
                      <td className="px-3 py-2">
                        <div>{entry.name}</div>
                        {entry.gstTin && (
                          <div className="text-xs text-gray-500">Gst No: {entry.gstTin}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">{entry.billNo || "—"}</td>
                      <td className="px-3 py-2">{entry.vehicleNo || "—"}</td>
                      <td className="px-3 py-2">{entry.employee || "—"}</td>
                      <td className="px-3 py-2 font-medium">{entry.product || "—"}</td>
                      <td className="px-3 py-2">{entry.collectionMode || "—"}</td>
                      <td className="px-3 py-2">{entry.collectionType || "—"}</td>
                      <td className="px-3 py-2">{(entry.price ?? 0).toFixed(3)}</td>
                      <td className="px-3 py-2">{(entry.qty ?? 0).toFixed(3)}</td>
                      <td className="px-3 py-2">{(entry.amount ?? 0).toFixed(3)}</td>
                      <td className="px-3 py-2">{(entry.discount ?? 0).toFixed(3)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => {
                            PrintService.printBill({
                              billNo: entry.billNo || entry.id?.slice(-8),
                              date: entry.date,
                              productName: entry.product,
                              qty: entry.qty,
                              price: entry.price,
                              total: entry.amount,
                              discount: entry.discount,
                              customerName: entry.name,
                              vehicleNo: entry.vehicleNo,
                              employeeName: entry.employee,
                              mobile: entry.mobileNumber
                            });
                          }}
                          className="text-indigo-600 hover:underline mr-3 font-medium"
                        >
                          Print
                        </button>
                        <button type="button" onClick={() => handleEdit(entry)} className="text-blue-600 hover:underline mr-2">Edit</button>
                        <button type="button" onClick={() => handleDelete(entry.id)} className="text-red-600 hover:underline">Del</button>
                      </td>
                      <td className="px-3 py-2">{entry.shift || "—"}</td>
                      <td className="px-3 py-2 max-w-[120px] truncate">{entry.description || "—"}</td>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {entry.createdAt
                          ? `Created: ${format(new Date(entry.createdAt), "dd-MM-yyyy hh:mm a")}`
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t bg-gray-50 text-sm text-gray-600">
            Showing {visibleRows.length > 0 ? 1 : 0} to {visibleRows.length} of {filtered.length} entries
          </div>
        </div>
      </div>

      {gstPopup && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h3 className="font-bold text-lg mb-2">Fill GST</h3>
            <input
              className="w-full border rounded px-3 py-2 mb-4"
              placeholder="GST / TIN number"
              value={gstInput}
              onChange={(e) => setGstInput(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setGstPopup(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button
                type="button"
                onClick={() => { setField("gstTin", gstInput); setGstPopup(false); }}
                className="px-4 py-2 bg-blue-900 text-white rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

